// Live Monitoring - Real-time chat and Q&A feeds
import React, { useState, useEffect, useRef } from 'react';
import { db } from '../services/firebase';
import {
    collection,
    collectionGroup,
    query,
    orderBy,
    limit,
    onSnapshot
} from 'firebase/firestore';
import { seedDefaultChatRooms } from '../utils/seedDefaults';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Avatar,
    Chip,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Badge,
    Divider,
    CircularProgress,
    Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ChatIcon from '@mui/icons-material/Chat';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import WarningIcon from '@mui/icons-material/Warning';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

// Crisis keywords to highlight
const CRISIS_KEYWORDS = ['suicide', 'kill myself', 'end my life', 'self harm', 'hurt myself', 'don\'t want to live'];

const LiveMonitoring = () => {
    const [tabValue, setTabValue] = useState(0);
    const [rooms, setRooms] = useState([]);
    const [roomsLoading, setRoomsLoading] = useState(true);
    const [roomsError, setRoomsError] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState('');
    const [messages, setMessages] = useState([]);
    const [messagesLoading, setMessagesLoading] = useState(false);
    const [messagesError, setMessagesError] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [questionsLoading, setQuestionsLoading] = useState(false);
    const [questionsError, setQuestionsError] = useState(null);
    const [answers, setAnswers] = useState([]);
    const [answersError, setAnswersError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const messagesEndRef = useRef(null);

    // Subscribe immediately so the dropdown is never blocked on seeding.
    useEffect(() => {
        setRoomsLoading(true);
        setRoomsError(null);
        seedDefaultChatRooms(db).catch((error) => {
            console.warn('Could not seed default rooms:', error);
        });

        const unsubscribe = onSnapshot(
            collection(db, 'chat_rooms'),
            (snapshot) => {
                const roomsData = snapshot.docs
                    .map((docSnap) => ({ ...docSnap.data(), id: docSnap.id }))
                    .sort((a, b) => (a.name || a.id).localeCompare(b.name || b.id));
                setRooms(roomsData);
                setRoomsError(null);
                setSelectedRoom((prev) => {
                    if (prev && roomsData.some((room) => room.id === prev)) return prev;
                    return roomsData[0]?.id || '';
                });
                setRoomsLoading(false);
            },
            (error) => {
                console.error('Error loading rooms:', error);
                setRoomsError(error.message || 'Failed to load rooms');
                setRooms([]);
                setSelectedRoom('');
                setRoomsLoading(false);
            }
        );
        return () => unsubscribe();
    }, []);

    const messageTimeValue = (msg) => {
        const t = msg.createdAt || msg.timestamp;
        if (!t) return 0;
        if (typeof t.toMillis === 'function') return t.toMillis();
        const parsed = new Date(t).getTime();
        return Number.isFinite(parsed) ? parsed : 0;
    };

    // Subscribe to messages when room changes. Fall back to an unordered
    // query if createdAt is missing on older docs (index / field errors).
    useEffect(() => {
        if (!selectedRoom || tabValue !== 0) return;

        setMessagesLoading(true);
        setMessagesError(null);
        setMessages([]);

        let unsubscribe = () => {};
        const attach = (ordered) => {
            const col = collection(db, 'chat_rooms', selectedRoom, 'messages');
            const q = ordered
                ? query(col, orderBy('createdAt', 'desc'), limit(50))
                : query(col, limit(50));
            unsubscribe = onSnapshot(q, (snapshot) => {
                const messagesData = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data()
                }));
                messagesData.sort((a, b) => messageTimeValue(a) - messageTimeValue(b));
                setMessages(messagesData);
                setMessagesError(null);
                setMessagesLoading(false);
            }, (error) => {
                console.error('Error loading messages:', error);
                if (ordered) {
                    unsubscribe();
                    attach(false);
                    return;
                }
                setMessagesError(error.message || 'Failed to load messages');
                setMessages([]);
                setMessagesLoading(false);
            });
        };
        attach(true);
        return () => unsubscribe();
    }, [selectedRoom, tabValue]);

    // Subscribe to Q&A
    useEffect(() => {
        if (tabValue !== 1) return;

        setQuestionsLoading(true);
        setQuestionsError(null);
        let unsubscribe = () => {};
        const attach = (ordered) => {
            const col = collection(db, 'qa_questions');
            const q = ordered
                ? query(col, orderBy('createdAt', 'desc'), limit(30))
                : query(col, limit(30));
            unsubscribe = onSnapshot(q, (snapshot) => {
                const questionsData = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    ...docSnap.data()
                }));
                questionsData.sort((a, b) => {
                    const ts = (item) => {
                        const t = item.createdAt;
                        if (!t) return 0;
                        if (typeof t.toMillis === 'function') return t.toMillis();
                        const parsed = new Date(t).getTime();
                        return Number.isFinite(parsed) ? parsed : 0;
                    };
                    return ts(b) - ts(a);
                });
                setQuestions(questionsData);
                setQuestionsError(null);
                setQuestionsLoading(false);
            }, (error) => {
                console.error('Error loading Q&A:', error);
                if (ordered) {
                    unsubscribe();
                    attach(false);
                    return;
                }
                setQuestionsError(error.message || 'Failed to load Q&A');
                setQuestions([]);
                setQuestionsLoading(false);
            });
        };
        attach(true);
        return () => unsubscribe();
    }, [tabValue]);

    // Recent Q&A answers (doctor/staff replies) for the same live feed.
    useEffect(() => {
        if (tabValue !== 1) return;
        setAnswersError(null);
        let unsubscribe = () => {};
        const attach = (ordered) => {
            const col = collectionGroup(db, 'answers');
            const q = ordered
                ? query(col, orderBy('createdAt', 'desc'), limit(30))
                : query(col, limit(30));
            unsubscribe = onSnapshot(q, (snapshot) => {
                const answersData = snapshot.docs.map((docSnap) => ({
                    id: docSnap.id,
                    questionId: docSnap.ref.parent.parent?.id,
                    ...docSnap.data()
                }));
                answersData.sort((a, b) => {
                    const ts = (item) => {
                        const t = item.createdAt;
                        if (!t) return 0;
                        if (typeof t.toMillis === 'function') return t.toMillis();
                        const parsed = new Date(t).getTime();
                        return Number.isFinite(parsed) ? parsed : 0;
                    };
                    return ts(b) - ts(a);
                });
                setAnswers(answersData);
                setAnswersError(null);
            }, (error) => {
                console.error('Error loading Q&A answers:', error);
                if (ordered) {
                    unsubscribe();
                    attach(false);
                    return;
                }
                setAnswersError(error.message || 'Failed to load answers');
                setAnswers([]);
            });
        };
        attach(true);
        return () => unsubscribe();
    }, [tabValue]);

    const messageText = (msg) => msg.content || msg.text || '';
    const messageTime = (msg) => msg.createdAt || msg.timestamp;

    const containsCrisisKeyword = (text) => {
        if (!text) return false;
        const lowerText = text.toLowerCase();
        return CRISIS_KEYWORDS.some(keyword => lowerText.includes(keyword));
    };

    // Filter by search
    const filteredMessages = messages.filter(msg =>
        !searchQuery ||
        messageText(msg).toLowerCase().includes(searchQuery.toLowerCase()) ||
        msg.senderName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredQuestions = questions.filter(q =>
        !searchQuery ||
        q.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        q.body?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredAnswers = answers.filter(a =>
        !searchQuery ||
        a.body?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        a.authorName?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    return (
        <Box>
            {/* Header */}
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                Live Monitoring
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Real-time community activity feeds
            </Typography>

            {/* Tabs */}
            <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
                <Tab
                    label="Chat Rooms"
                    icon={<ChatIcon />}
                    iconPosition="start"
                />
                <Tab
                    label="Q&A"
                    icon={<QuestionAnswerIcon />}
                    iconPosition="start"
                />
            </Tabs>

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                {tabValue === 0 && (
                    <FormControl sx={{ minWidth: 280, flex: { xs: '1 1 100%', sm: '0 0 320px' } }}>
                        <InputLabel id="room-select-label">Select Room</InputLabel>
                        <Select
                            labelId="room-select-label"
                            value={rooms.some((room) => room.id === selectedRoom) ? selectedRoom : ''}
                            label="Select Room"
                            onChange={(e) => setSelectedRoom(e.target.value)}
                        >
                            <MenuItem value="" disabled>
                                {roomsLoading
                                    ? 'Loading rooms…'
                                    : (roomsError || (rooms.length === 0 ? 'No active rooms found' : 'Select a room'))}
                            </MenuItem>
                            {rooms.map((room) => (
                                <MenuItem key={room.id} value={room.id}>
                                    {room.name || room.id}
                                    {room.isPalsRoom ? ' (PALS)' : ''}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}
                <TextField
                    sx={{ flex: 1, minWidth: 220 }}
                    placeholder="Search messages..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        )
                    }}
                />
            </Box>

            {tabValue === 0 && roomsError && (
                <Alert severity="error" sx={{ mb: 2 }}>{roomsError}</Alert>
            )}
            {tabValue === 0 && roomsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading rooms…</Typography>
                </Box>
            )}

            {/* Content */}
            <Card sx={{ height: 'calc(100vh - 320px)', overflow: 'hidden' }}>
                <CardContent sx={{ height: '100%', overflow: 'auto', p: 0 }}>
                    {tabValue === 0 ? (
                        // Chat Messages
                        filteredMessages.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                                <ChatIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                                <Typography>
                                    {messagesLoading || roomsLoading
                                        ? 'Loading…'
                                        : messagesError
                                            ? messagesError
                                            : !selectedRoom
                                            ? (roomsError || 'No active rooms found')
                                            : searchQuery
                                                ? 'No messages match your search'
                                                : 'No messages in this room yet'}
                                </Typography>
                            </Box>
                        ) : (
                        <List>
                            {filteredMessages.map((msg) => {
                                const isCrisis = containsCrisisKeyword(messageText(msg));
                                return (
                                    <ListItem
                                        key={msg.id}
                                        sx={{
                                            bgcolor: isCrisis ? 'rgba(211,47,47,0.1)' : 'transparent',
                                            borderLeft: isCrisis ? '4px solid #D32F2F' : 'none'
                                        }}
                                    >
                                        <ListItemAvatar>
                                            <Badge
                                                overlap="circular"
                                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                                badgeContent={
                                                    isCrisis ? <WarningIcon sx={{ fontSize: 14, color: '#D32F2F' }} /> : null
                                                }
                                            >
                                                <Avatar src={msg.senderPhoto}>
                                                    {msg.senderName?.[0] || '?'}
                                                </Avatar>
                                            </Badge>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="subtitle2">
                                                        {msg.isAnonymous ? 'Anonymous' : msg.senderName}
                                                    </Typography>
                                                    {msg.senderRole && (
                                                        <Chip size="small" label={msg.senderRole} />
                                                    )}
                                                    <Typography variant="caption" color="text.secondary">
                                                        {formatTime(messageTime(msg))}
                                                    </Typography>
                                                    {isCrisis && (
                                                        <Chip
                                                            size="small"
                                                            label="Crisis Keywords"
                                                            color="error"
                                                            icon={<WarningIcon />}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            secondary={messageText(msg)}
                                        />
                                    </ListItem>
                                );
                            })}
                            <div ref={messagesEndRef} />
                        </List>
                        )
                    ) : (
                        // Q&A Feed
                        filteredQuestions.length === 0 && filteredAnswers.length === 0 ? (
                            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
                                <QuestionAnswerIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
                                <Typography>
                                    {questionsLoading
                                        ? 'Loading Q&A…'
                                        : questionsError
                                            ? questionsError
                                            : searchQuery
                                                ? 'No questions match your search'
                                                : 'No Q&A questions yet'}
                                </Typography>
                            </Box>
                        ) : (
                        <>
                        <List>
                            {filteredQuestions.map((question) => {
                                const isCrisis = containsCrisisKeyword(question.title) || containsCrisisKeyword(question.body);
                                const tagLabel = Array.isArray(question.tags) && question.tags.length
                                    ? question.tags[0]
                                    : (question.communityId || question.authorRole || null);
                                return (
                                    <React.Fragment key={question.id}>
                                        <ListItem
                                            sx={{
                                                bgcolor: isCrisis ? 'rgba(211,47,47,0.1)' : 'transparent',
                                                borderLeft: isCrisis ? '4px solid #D32F2F' : 'none'
                                            }}
                                        >
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="subtitle1" fontWeight={500}>
                                                            {question.title}
                                                        </Typography>
                                                        {question.authorRole && question.authorRole !== 'user' && (
                                                            <Chip size="small" label={question.authorRole} color="success" />
                                                        )}
                                                        {isCrisis && (
                                                            <Chip size="small" label="Review" color="error" />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box>
                                                        <Typography variant="body2" color="text.secondary" sx={{
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            display: '-webkit-box',
                                                            WebkitLineClamp: 2,
                                                            WebkitBoxOrient: 'vertical'
                                                        }}>
                                                            {question.body}
                                                        </Typography>
                                                        <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                            {tagLabel && (
                                                                <Chip
                                                                    size="small"
                                                                    label={tagLabel}
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            <Typography variant="caption" color="text.secondary">
                                                                {question.answerCount || 0} answers • {formatTime(question.createdAt)}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        <Divider />
                                    </React.Fragment>
                                );
                            })}
                        </List>
                        {filteredAnswers.length > 0 && (
                            <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Recent answers
                                </Typography>
                                {answersError && (
                                    <Alert severity="warning" sx={{ mb: 1 }}>{answersError}</Alert>
                                )}
                                <List disablePadding>
                                    {filteredAnswers.map((answer) => (
                                        <ListItem key={answer.id} alignItems="flex-start" sx={{ px: 0 }}>
                                            <ListItemAvatar>
                                                <Avatar>{answer.authorName?.[0] || '?'}</Avatar>
                                            </ListItemAvatar>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                        <Typography variant="subtitle2">
                                                            {answer.isAnonymous ? 'Anonymous' : (answer.authorName || 'Member')}
                                                        </Typography>
                                                        {answer.expertBadge && (
                                                            <Chip size="small" label={answer.expertBadge} color="success" />
                                                        )}
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatTime(answer.createdAt)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={answer.body}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                        </>
                        )
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default LiveMonitoring;
